import { coreApiAuthedClient } from '@/src/shared/api/clients';
import { toApiError } from '@/src/shared/api/accounts/http';
import type {
  HouseholdAccount,
  HouseholdCategory,
  HouseholdCategoryTotal,
  HouseholdCurrencySummary,
  HouseholdMovementUpdate,
} from './dto';

const BASE = '/v1/households/me';

async function run<T>(call: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await call()).data;
  } catch (err) {
    toApiError(err);
  }
}

const id = (value: string) => encodeURIComponent(value);

export const shareAccount = (accountId: string) =>
  run(() => coreApiAuthedClient.PUT({ path: `${BASE}/shared-accounts/${id(accountId)}` }));

export const unshareAccount = (accountId: string) =>
  run(() => coreApiAuthedClient.DELETE({ path: `${BASE}/shared-accounts/${id(accountId)}` }));

export const getHouseholdAccounts = () =>
  run(() => coreApiAuthedClient.GET<unknown, HouseholdAccount[]>({ path: `${BASE}/accounts` }));

export const getHouseholdSummary = async () =>
  (
    await run(() =>
      coreApiAuthedClient.GET<unknown, { items: HouseholdCurrencySummary[] }>({
        path: `${BASE}/summary`,
      }),
    )
  ).items;

export const getHouseholdCategories = () =>
  run(() => coreApiAuthedClient.GET<unknown, HouseholdCategory[]>({ path: `${BASE}/categories` }));

export const updateHouseholdMovement = (movementId: string, body: HouseholdMovementUpdate) =>
  run(() =>
    coreApiAuthedClient.PATCH<HouseholdMovementUpdate, unknown>({
      path: `${BASE}/movements/${id(movementId)}`,
      data: body,
    }),
  );

export const syncHouseholdAccount = (accountId: string) =>
  run(() =>
    coreApiAuthedClient.POST<Record<string, never>, { status: string }>({
      path: `${BASE}/accounts/${id(accountId)}/sync`,
      data: {},
    }),
  );

/** `account_id` narrows the totals to one visible account. */
export const getHouseholdCategoryTotals = async (params: {
  date_from: string;
  date_to: string;
  account_id?: string;
}) =>
  (
    await run(() =>
      coreApiAuthedClient.GET<typeof params, { items: HouseholdCategoryTotal[] }>({
        path: `${BASE}/category-totals`,
        params,
      }),
    )
  ).items;
